import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { X } from "lucide-react";
import { Button } from "@/components/primitives/Button";
import { fmtDuration, thumbnailUrl, type Montage, type MontageText, type MontageTextInput, type TextPosition, type TextSize } from "@/lib/montageApi";

export const TEXT_MAX_CHARS = 150;
// Mirrors backend TEXT_SIZE_PCT / margins (approximate CSS preview of the libass burn-in).
export const TEXT_SIZE_PCT: Record<TextSize, number> = { small: 4.5, medium: 6.5, large: 9 };
const POSITIONS: TextPosition[] = ["top", "center", "bottom"];
const SIZES: TextSize[] = ["small", "medium", "large"];
const PREVIEW_H = 224;

interface Props {
  m: Montage;
  initial?: MontageText | null;
  onSave: (body: MontageTextInput) => Promise<void>;
  onClose: () => void;
}

function Preview({ m, text, position, size, start, end, at }: { m: Montage } & MontageTextInput & { at: number }) {
  const { t } = useTranslation();
  const item = useMemo(() => {
    let acc = 0;
    for (const it of m.items) {
      if (at < acc + it.effective_duration) return it;
      acc += it.effective_duration;
    }
    return m.items[m.items.length - 1] ?? null;
  }, [m.items, at]);
  const visible = !!text.trim() && at >= start && at < end;
  const place = position === "top" ? "items-start" : position === "bottom" ? "items-end" : "items-center";
  return (
    <div className="flex flex-col items-center gap-2">
      <div
        data-testid="text-preview-frame"
        className="relative overflow-hidden rounded-md border border-[#2E7BFF]/30 bg-black"
        style={{ height: PREVIEW_H, aspectRatio: m.aspect_ratio === "16:9" ? "16 / 9" : "9 / 16" }}
      >
        {item?.thumbnail_path && <img src={thumbnailUrl(m.id, item.id)} alt="" className="absolute inset-0 h-full w-full object-contain" />}
        <div className={`absolute inset-0 flex ${place} justify-center`} style={{ padding: `${PREVIEW_H * 0.07}px 6%` }}>
          {visible && (
            <span
              data-testid="text-preview-text"
              className="w-full break-words text-center font-bold leading-tight text-white"
              style={{
                fontSize: (PREVIEW_H * TEXT_SIZE_PCT[size]) / 100,
                fontFamily: "'Liberation Sans', Arial, Helvetica, sans-serif",
                whiteSpace: "pre-line",
                textShadow: "0 0 2px #000, 0 0 2px #000, 1px 1px 0 #000, -1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 0 2px 3px rgba(0,0,0,.7)",
              }}
            >
              {text}
            </span>
          )}
        </div>
      </div>
      <p className="text-[11px] text-muted-foreground">{t("textOverlay.previewHint")}</p>
    </div>
  );
}

export function TextDialog({ m, initial, onSave, onClose }: Props) {
  const { t } = useTranslation();
  const total = Math.max(0, m.total_duration);
  const [text, setText] = useState(initial?.text ?? "");
  const [position, setPosition] = useState<TextPosition>(initial?.position ?? "bottom");
  const [size, setSize] = useState<TextSize>(initial?.size ?? "medium");
  const [start, setStart] = useState(String(initial?.start ?? 0));
  const [end, setEnd] = useState(String(initial?.end ?? Math.min(5, total)));
  const [at, setAt] = useState(initial?.start ?? 0);
  const [saving, setSaving] = useState(false);
  const s = Number(start), e = Number(end);
  useEffect(() => { if (Number.isFinite(s)) setAt(Math.max(0, s)); }, [s]);

  const save = async () => {
    if (!text.trim()) { toast.error(t("textOverlay.emptyText")); return; }
    if (text.trim().length > TEXT_MAX_CHARS) { toast.error(t("textOverlay.tooLong", { max: TEXT_MAX_CHARS })); return; }
    if (!Number.isFinite(s) || !Number.isFinite(e) || s < 0 || e <= s || e > total + 0.05) { toast.error(t("textOverlay.invalidTiming")); return; }
    setSaving(true);
    try {
      await onSave({ text: text.trim(), position, size, start: s, end: Math.min(e, total) });
    } catch { /* toast shown by caller, dialog stays open */ } finally {
      setSaving(false);
    }
  };

  const seg = (active: boolean) =>
    "rounded-md border px-2.5 py-1 text-[11px] uppercase tracking-wider transition-colors disabled:opacity-40 " +
    (active ? "border-[#2E7BFF] bg-[#2E7BFF]/15 text-[#7FD4FF]" : "border-[#2E7BFF]/25 text-muted-foreground hover:text-foreground");

  return (
    <div className="fixed inset-0 z-50 flex justify-center overflow-y-auto bg-black/70 p-4" onClick={saving ? undefined : onClose}>
      <div data-testid="text-dialog" onClick={(ev) => ev.stopPropagation()} className="glass my-auto w-full max-w-2xl rounded-2xl p-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-display text-base font-semibold uppercase tracking-wide">{initial ? t("textOverlay.edit") : t("textOverlay.add")}</h2>
          <button data-testid="text-dialog-close" onClick={onClose} disabled={saving} className="text-muted-foreground hover:text-foreground disabled:opacity-50">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="grid gap-5 sm:grid-cols-[1fr_auto]">
          <div className="space-y-4">
            <label className="block space-y-1.5">
              <span className="flex items-center justify-between text-xs text-muted-foreground">
                <span className="uppercase tracking-widest">{t("textOverlay.content")}</span>
                <span data-testid="text-dialog-count" className="font-mono">{text.length}/{TEXT_MAX_CHARS}</span>
              </span>
              <textarea
                data-testid="text-dialog-input"
                value={text}
                maxLength={TEXT_MAX_CHARS}
                rows={2}
                disabled={saving}
                placeholder={t("textOverlay.placeholder")}
                onChange={(ev) => setText(ev.target.value)}
                className="field-premium resize-none px-3 py-2 text-sm"
              />
            </label>
            <div className="space-y-1.5">
              <span className="block text-xs uppercase tracking-widest text-muted-foreground">{t("textOverlay.position")}</span>
              <div className="flex flex-wrap gap-1.5">
                {POSITIONS.map((p) => (
                  <button key={p} type="button" data-testid={`text-dialog-pos-${p}`} disabled={saving} onClick={() => setPosition(p)} className={seg(position === p)}>
                    {t(`textOverlay.${p}`)}
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-1.5">
              <span className="block text-xs uppercase tracking-widest text-muted-foreground">{t("textOverlay.size")}</span>
              <div className="flex flex-wrap gap-1.5">
                {SIZES.map((z) => (
                  <button key={z} type="button" data-testid={`text-dialog-size-${z}`} disabled={saving} onClick={() => setSize(z)} className={seg(size === z)}>
                    {t(`textOverlay.${z}`)}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <label className="flex items-center gap-1 text-muted-foreground">
                {t("textOverlay.start")}
                <input data-testid="text-dialog-start" type="number" min={0} max={total} step={0.1} value={start} disabled={saving}
                  onChange={(ev) => setStart(ev.target.value)} className="field-premium h-8 w-24 !rounded-md px-2 text-xs" />
              </label>
              <label className="flex items-center gap-1 text-muted-foreground">
                {t("textOverlay.end")}
                <input data-testid="text-dialog-end" type="number" min={0} max={total} step={0.1} value={end} disabled={saving}
                  onChange={(ev) => setEnd(ev.target.value)} className="field-premium h-8 w-24 !rounded-md px-2 text-xs" />
              </label>
              <span className="text-muted-foreground">s · {t("montageEditor.duration")} {fmtDuration(total)}</span>
            </div>
            <label className="block space-y-1.5">
              <span className="flex items-center justify-between text-xs text-muted-foreground">
                <span className="uppercase tracking-widest">{t("textOverlay.preview")}</span>
                <span data-testid="text-dialog-at" className="font-mono text-foreground">{fmtDuration(at)}</span>
              </span>
              <input data-testid="text-dialog-scrub" type="range" min={0} max={total} step={0.1} value={at} onChange={(ev) => setAt(Number(ev.target.value))}
                className="h-1.5 w-full cursor-pointer appearance-none rounded-full bg-white/10 accent-[#2E7BFF]" />
            </label>
          </div>
          <Preview m={m} text={text} position={position} size={size} start={Number.isFinite(s) ? s : 0} end={Number.isFinite(e) ? e : 0} at={at} />
        </div>
        <div className="mt-5 flex flex-wrap justify-end gap-2">
          <Button data-testid="text-dialog-cancel" variant="secondary" size="sm" onClick={onClose} disabled={saving}>{t("common.cancel")}</Button>
          <Button data-testid="text-dialog-save" size="sm" onClick={save} disabled={saving || !text.trim()}>{t("common.save")}</Button>
        </div>
      </div>
    </div>
  );
}
